// @ts-check
// When updating marker for using new create function, make sure that anchorPositions work correctly for existing marker

/// <reference types="google.maps" />

import {gmAPI} from './Map';
import {markRaw} from 'vue';

// Constant values
const WIDTH = 28;
const HEIGHT = 28;

// Default values
let baseUrl = 'https://storage.googleapis.com/beyond_maps_public/';
const folderName = process.env.NODE_ENV === 'production' ? 'Marker_Icons_Prod/' : 'Marker_Icons_Staging/';
baseUrl = `${baseUrl}${folderName}`;
const defaultSize = new gmAPI.Size(WIDTH, HEIGHT);
const defaultOrigin = new gmAPI.Point(0, 0);
const anchorBottom = new gmAPI.Point(WIDTH / 2, HEIGHT);

// all svgs are imported here so that we don't need to use "require" in the component

import BalloonIcon from '@/components/svgs/MarkerIcons/Balloons/BalloonIcon.vue';
import BalloonFlatIcon from '@/components/svgs/MarkerIcons/Balloons/BalloonFlatIcon.vue';
import PortfolioFlatIcon from '@/components/svgs/MarkerIcons/Portfolios/PortfolioFlatIcon.vue';
import CircleIcon from '@/components/svgs/MarkerIcons/Circles/CircleIcon.vue';
import CompassIcon from '@/components/svgs/MarkerIcons/Compasses/CompassIcon.vue';
import BuildingIcon from '@/components/svgs/MarkerIcons/Buildings/BuildingIcon.vue';
import AcquisitionIcon from '@/components/svgs/MarkerIcons/Acquisitions/AcquisitionIcon.vue';
import PolygonIcon from '@/components/svgs/PolygonIcon.vue';
import AccessRouteIcon from '@/components/svgs/AccessRouteIcon.vue';
import PolylineIcon from '@/components/svgs/PolylineIcon.vue';
import LabelIcon from '@/components/svgs/LabelIcon.vue';
import KMZIcon from '@/components/svgs/KMZIcon.vue';
import ImageIcon from '@/components/svgs/ImageIcon.vue';
import OverlaysIcon from '@/components/svgs/MenuIcons/OverlaysIcon.vue';
import LayersIcon from '@/components/svgs/MenuIcons/LayersIcon.vue';
import BlockPlanIcon from '@/components/svgs/ToolboxIcons/BlockPlanIcon.vue';
import FolderIcon from '@/components/svgs/FolderIcon.vue';
import DataCenterProjectIcon from '@/components/svgs/MarkerIcons/DataCenterProject/DataCenterProjectIcon.vue';
import PortfolioIcon from '@/components/svgs/MarkerIcons/Portfolios/PortfolioIcon.vue';

/**
 * @typedef {google.maps.Icon & {svg?: import('vue').Component, hex?: string}} BmapsIcon
 */

/**
 * @private
 * @type {Map<string, BmapsIcon>}
 */
const markerIconMap = new Map();
const featureIconMap = new Map();

const defaultOpts = {
  size: defaultSize,
  scaledSize: defaultSize,
  origin: defaultOrigin,
  anchor: anchorBottom,
};

const yellowHex = '#ffea00';
const blackHex = '#414042';
const grayHex = '#a7a9ac';
const orangeHex = '#ef8a22';
const greenHex = '#00a14b';
const brownHex = '#724417';
const navyHex = '#405866';
const darkerBlueHex = '#116c99';

/** @type {Array<[string, string, import('vue').Component]>} */
const markerIcons = [
  ['Blue-Balloon', '#0288d1', BalloonIcon],
  ['Cyan-Balloon', '#54d4f7', BalloonIcon],
  ['Darker-Blue-Balloon', darkerBlueHex, BalloonIcon],
  ['Red-Balloon', '#ed1c24', BalloonIcon],
  ['Green-Balloon', greenHex, BalloonIcon],
  ['Purple-Balloon', '#5a3b8c', BalloonIcon],
  ['Yellow-Balloon', yellowHex, BalloonIcon],
  ['Orange-Circle', orangeHex, CircleIcon],
  ['Yellow-Circle', yellowHex, CircleIcon],
  ['Black-Circle', blackHex, CircleIcon],
  ['Gray-Circle', grayHex, CircleIcon],
  ['Brown-Circle', brownHex, CircleIcon],
  ['Navy-Circle', navyHex, CircleIcon],
  ['Green-Compass-Up', greenHex, CompassIcon],
  ['Orange-Compass-Up', orangeHex, CompassIcon],
  ['Yellow-Compass-Up', yellowHex, CompassIcon],
  ['Black-Compass-Up', blackHex, CompassIcon],
  ['Gray-Compass-Up', grayHex, CompassIcon],
  ['Brown-Compass-Up', brownHex, CompassIcon],
  ['Navy-Compass-Up', navyHex, CompassIcon],
  ['Orange-Multi-Family', orangeHex, BuildingIcon],
  ['Yellow-Multi-Family', yellowHex, BuildingIcon],
  ['Black-Multi-Family', blackHex, BuildingIcon],
  ['Gray-Multi-Family', grayHex, BuildingIcon],
  ['Navy-Multi-Family', navyHex, BuildingIcon],
  ['Orange-Acquisition', orangeHex, AcquisitionIcon],
  ['Yellow-Acquisition', yellowHex, AcquisitionIcon],
  ['Black-Acquisition', blackHex, AcquisitionIcon],
  ['Gray-Acquisition', grayHex, AcquisitionIcon],
  ['Brown-Acquisition', brownHex, AcquisitionIcon],
  ['Navy-Acquisition', navyHex, AcquisitionIcon],
  ['Black-DataCenterProject', blackHex, DataCenterProjectIcon],
  ['Brown-DataCenterProject', brownHex, DataCenterProjectIcon],
  ['Gray-DataCenterProject', grayHex, DataCenterProjectIcon],
  ['Orange-DataCenterProject', orangeHex, DataCenterProjectIcon],
  ['Yellow-DataCenterProject', yellowHex, DataCenterProjectIcon],
  ['Navy-DataCenterProject', navyHex, DataCenterProjectIcon],
  ['Darker-Blue-Portfolio', darkerBlueHex, PortfolioIcon],
  ['Blue-Balloon-Flat', darkerBlueHex, BalloonFlatIcon],
  ['LightBlue-Balloon-Flat', '#54d4f7', BalloonFlatIcon],
  ['Red-Balloon-Flat', '#ed1c24', BalloonFlatIcon],
  ['Green-Balloon-Flat', greenHex, BalloonFlatIcon],
  ['Purple-Balloon-Flat', '#5a3b8c', BalloonFlatIcon],
  ['Yellow-Balloon-Flat', yellowHex, BalloonFlatIcon],
  ['Blue-Portfolio-Flat', darkerBlueHex, PortfolioFlatIcon],
];
markerIcons.forEach(([name, hex, icon]) => {
  markerIconMap.set(name, {
    url: `${baseUrl}${name}.png`,
    svg: markRaw(icon),
    hex,
    ...defaultOpts,
  });
});

markerIconMap.set('Blue-Dot', {
  url: `${baseUrl}Blue-Dot.png`,
  ...defaultOpts,
});

/** @type {Array<[string, import('vue').Component]>} */
const featureIcons = [
  ['Overlay-Icon', OverlaysIcon],
  ['Layer-Icon', LayersIcon],
  ['Polygon-Icon', PolygonIcon],
  ['AccessRoute-Icon', AccessRouteIcon],
  ['Polyline-Icon', PolylineIcon],
  ['Label-Icon', LabelIcon],
  ['KMZ-Icon', KMZIcon],
  ['Image-Icon', ImageIcon],
  ['BlockPlan-Icon', BlockPlanIcon],
  ['Folder-Icon', FolderIcon],
];
featureIcons.forEach(([name, icon]) => featureIconMap.set(name, {svg: markRaw(icon)}));

export {
  markerIconMap,
  featureIconMap,
};
